import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExperiment } from '../../contexts/ExperimentContext';
import { useTracker } from '../../contexts/TrackerContext';
import { savePhaseData } from '../../services/api';
import MicroSurvey from '../common/MicroSurvey';
import DeviceScenarioVisual from '../device/DeviceScenarioVisual';
import './DeviceTask.css';
import { deviceScenarios, getScenariosByCongruence, balanceScenarios, DeviceScenario as ImportedDeviceScenario } from '../../data/deviceScenariosWithCongruence';
import { SeededRandom, generateOrderSeed } from '../../utils/seededRandom';
import { getOrgConfig } from '../../data/orgConfigs';
import { resolveOrgTemplates } from '../../utils/resolveOrgTemplates';

interface DeviceScenario extends ImportedDeviceScenario {
  // Additional UI properties not in base interface
  title?: string;
  context?: string;
  image?: string;
  visualCues?: {
    warningSign?: boolean;
    securityBadge?: boolean;
    urgencyIndicator?: boolean;
    trustSeals?: string[];
  };
  metadata?: {
    attackVector?: string;
    socialEngineeringTactic?: string;
    cognitiveVulnerability?: string;
    psychologicalDistance?: string;
  };
  // User decision
  decision?: 'proceed' | 'decline' | null;
  // Additional risk indicators (legacy support)
  riskIndicators?: string[];
  legitimacyIndicators?: string[];
}

interface DeviceMicroSurveyResponse {
  reason: string;
  customReason?: string;
  reasonCategory?: 'gist' | 'verbatim' | 'other' | 'task_need' | 'external_pressure' | 'motivation' | 'risk_assessment';
  confidence?: number;
  metacognitiveCertainty?: number;
  perceivedTimePressure?: number;
  rankedReasons?: Array<{
    rank: number;
    value: string;
    label: string;
    category: 'gist' | 'verbatim';
  }>;
  gistDominanceScore?: number;
  presentedOrder?: string[];
  selectedIndices?: number[];
  primaryReasonKeySelected?: string;
  routeSelected?: 'gist' | 'verbatim';
  gistOrder?: string[];
  verbatimOrder?: string[];
  selectedIndexWithinColumn?: number;
  decisionLatencyMs?: number;
  totalMicroSurveyTimeMs?: number;
  step1TimeMs?: number | null;
  step2TimeMs?: number | null;
}

const DeviceTask: React.FC = () => {
  const navigate = useNavigate();
  const { experimentData, updateProgress, updateFTTClassification, fttData } = useExperiment();
  const { tracker, trackHover, trackMouseMovement, classifyCurrentResponse } = useTracker();
  
  const [scenarios, setScenarios] = useState<DeviceScenario[]>([]);
  const [currentScenarioIndex, setCurrentScenarioIndex] = useState(0);
  const [responses, setResponses] = useState<{ [key: string]: any }>({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [hoveredRiskIndicator, setHoveredRiskIndicator] = useState<string | null>(null);
  const [showRiskDetails, setShowRiskDetails] = useState(false);
  const [showMicroSurvey, setShowMicroSurvey] = useState(false);
  const [pendingAction, setPendingAction] = useState<{scenario: DeviceScenario, actionTaken: 'proceed' | 'decline'} | null>(null);
  const phaseStartTime = useRef(Date.now());
  const scenarioStartTime = useRef(Date.now());
  const decisionStartTime = useRef(Date.now());
  
  // Helper functions for FTT context
  const calculateScenarioComplexity = (scenario: DeviceScenario): number => {
    let complexity = 0.5; // Base complexity
    
    // More risk indicators = more complex
    if (scenario.riskIndicators) {
      complexity += scenario.riskIndicators.length * 0.1;
    }
    
    // Conflicting indicators increase complexity
    if (scenario.legitimacyIndicators && scenario.riskIndicators) {
      complexity += 0.2;
    }
    
    // Metadata factors
    if (scenario.metadata?.socialEngineeringTactic) {
      complexity += 0.1;
    }
    
    return Math.min(complexity, 1);
  };
  
  const calculateDeviceCognitiveLoad = (): number => {
    const timeInTask = Date.now() - phaseStartTime.current;
    const responsesCount = Object.keys(responses).length;
    const recentHesitations = tracker.events.filter(
      e => e.eventType === 'hesitation' && e.timestamp > Date.now() - 30000
    ).length;
    
    // Factor in cross-domain fatigue if we have email data
    const emailFatigue = fttData.emailClassifications.size > 0 ? 0.2 : 0;
    
    const timeFactor = Math.min(timeInTask / (10 * 60 * 1000), 1);
    const responseFactor = Math.min(responsesCount / 10, 1);
    const hesitationFactor = Math.min(recentHesitations / 5, 1);
    
    return Math.min((timeFactor + responseFactor + hesitationFactor) / 3 + emailFatigue, 1);
  };

  // Helper functions for risk indicator descriptions
  const getRiskIndicatorDescription = (indicator: string): string => {
    const descriptions: { [key: string]: string } = {
      'unknownSource': 'Unknown or unverified source',
      'foundDevice': 'Physical device found in unsecured location',
      'potentialMalware': 'Potential malware or virus risk',
      'unexpectedPopup': 'Unexpected popup or notification',
      'unfamiliarDomain': 'Unfamiliar or suspicious domain name',
      'urgentLanguage': 'Uses urgent or pressuring language',
      'excessivePermissions': 'Requests excessive permissions',
      'suspiciousDomain': 'Domain doesn\'t match expected pattern',
      'unusualRequest': 'Unusual or unexpected request',
      'openNetwork': 'Open network without password protection',
      'noPassword': 'No authentication required',
      'publicLocation': 'Located in public or unsecured area',
      'similarName': 'Name similar to legitimate service',
      'unsolicited': 'Unsolicited or unexpected request',
      'unknownDevice': 'Unknown or unrecognized device',
      'publicSpace': 'Risk increased in public spaces',
      'suspiciousName': 'Suspicious or unusual naming',
      'noTicket': 'No support ticket or reference number',
      'browserPopup': 'Browser-based popup (not system)',
      'vague': 'Vague or unclear about specifics',
      'certificateError': 'Security certificate error or warning',
      'securityWarning': 'Browser security warning displayed',
      'proceedOption': 'Option to bypass security warning',
      'unexpectedUpdate': 'Unexpected update notification',
      'browserSource': 'Originates from browser, not system',
      'fearLanguage': 'Uses fear-inducing language',
      'unofficialSource': 'Not from official channels',
      'externalDomain': 'External or third-party domain',
      'tooGoodToBeTrue': 'Offer seems too good to be true',
      'noVerification': 'Cannot verify authenticity',
      'unusualApp': 'Unusual or unknown application',
      'unknownStore': 'Unknown app store or repository',
      'pressure': 'Time pressure or urgency',
      'lastMinute': 'Last-minute request',
      'unexpectedSender': 'Sender not previously known',
      'wrongService': 'Wrong service or platform used',
      'financialData': 'Involves financial or sensitive data',
      'lookalikeDomain': 'Domain resembles legitimate site',
      'publicUSB': 'Public USB charging station',
      'dataConnection': 'USB data connection (not power-only)',
      'unknown': 'Unknown or unverified source',
      'juiceJacking': 'Risk of juice jacking attack'
    };
    
    return descriptions[indicator] || indicator.replace(/([A-Z])/g, ' $1').trim();
  };

  const getLegitimacyIndicatorDescription = (indicator: string): string => {
    const descriptions: { [key: string]: string } = {
      'officialChannel': 'Sent through official channels',
      'ticketNumber': 'Has valid ticket or reference number',
      'expectedUpdate': 'Update was previously announced',
      'internalPortal': 'Uses internal company portal',
      'expectedPrompt': 'Expected based on your actions',
      'officialVPN': 'Official company VPN client',
      'workFromHome': 'Normal for remote work',
      'knownApplication': 'Recognized application',
      'systemTray': 'Appears in system tray (legitimate)',
      'regularSchedule': 'Follows regular update schedule',
      'knownAntivirus': 'Recognized antivirus software'
    };
    
    return descriptions[indicator] || indicator.replace(/([A-Z])/g, ' $1').trim();
  };

  useEffect(() => {
    // Track phase start
    tracker.track('phaseStart', {
      phase: 'device-task',
      condition: experimentData?.participant?.conditions || {},
      congruenceCondition: 'balanced-2x2x2x2' // All participants receive balanced distribution
    });

    // Generate device security scenarios based on condition - ONLY ONCE on mount
    const generatedScenarios = generateDeviceScenarios(experimentData?.participant?.id);
    setScenarios(generatedScenarios);

    return () => {
      // Track phase completion on unmount
      tracker.track('phaseEnd', {
        phase: 'device-task',
        duration: Date.now() - phaseStartTime.current,
        responses: responses
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // FIXED: Empty array - run ONLY on mount, never re-run

  useEffect(() => {
    // Reset scenario timer when changing scenarios
    scenarioStartTime.current = Date.now();
  }, [currentScenarioIndex]);

  const generateDeviceScenarios = (participantId?: string): DeviceScenario[] => {
    console.log('[DeviceTask] generateDeviceScenarios called');

    // Get balanced scenarios with Latin Square counterbalancing (2×2 design: Congruence × Truth)
    // Algorithm guarantees: 1 Congruent-Safe, 1 Congruent-Risky, 1 Incongruent-Safe, 1 Incongruent-Risky (4 total)
    // Domains rotate across participants for cross-participant balance
    let selectedScenarios = getScenariosByCongruence(participantId);
    console.log('[DeviceTask] After getScenariosByCongruence:', selectedScenarios.map(s => s.id));

    // Ensure balanced risky/safe distribution
    selectedScenarios = balanceScenarios(selectedScenarios);
    console.log('[DeviceTask] After balanceScenarios:', selectedScenarios.map(s => s.id));

    // Apply org-specific branding (domain names, WiFi names, locations, etc.)
    const orgConfig = getOrgConfig();
    selectedScenarios = selectedScenarios.map(s => resolveOrgTemplates(s, orgConfig));

    // Remove any duplicates based on ID
    const uniqueScenarios = selectedScenarios.filter((scenario, index, self) =>
      index === self.findIndex(s => s.id === scenario.id)
    );
    console.log('[DeviceTask] After deduplication:', uniqueScenarios.map(s => s.id));

    // Convert to DeviceScenario format with additional properties
    const scenarios: DeviceScenario[] = uniqueScenarios.map(scenario => ({
      ...scenario,
      title: scenario.name, // Use name as title
      context: scenario.details || scenario.description,
      decision: null,
      // Map threatIndicators/safetyIndicators to riskIndicators/legitimacyIndicators
      riskIndicators: scenario.threatIndicators,
      legitimacyIndicators: scenario.safetyIndicators,
      // Keep actionObj for UI
      actionObj: {
        text: 'Proceed',
        consequence: undefined
      }
    }));

    // Randomize order with seeded RNG for reproducibility
    const orderSeed = generateOrderSeed(participantId || 'unknown', {
      taskType: 'device',
      timestamp: Date.now(),
      congruence: 'balanced-2x2x2x2'
    });
    const rng = new SeededRandom(orderSeed);
    const finalScenarios = rng.shuffle(scenarios);
    console.log('[DeviceTask] Final scenarios (seeded):', finalScenarios.map(s => s.id));
    return finalScenarios;
  };

  const handleAction = (scenario: DeviceScenario, actionTaken: 'proceed' | 'decline') => {
    if (scenario.decision) {
      return; // Already processed, ignore new clicks
    }
    
    // Store the pending action and show micro-survey
    setPendingAction({ scenario, actionTaken });
    setShowMicroSurvey(true);
  };

  const processPendingAction = (surveyResponse?: DeviceMicroSurveyResponse) => {
    console.log('[DeviceTask] processPendingAction called');
    console.log('[DeviceTask] pendingAction:', pendingAction);
    console.log('[DeviceTask] surveyResponse:', surveyResponse);
    
    if (!pendingAction) {
      console.log('[DeviceTask] No pending action, returning');
      return;
    }
    
    const { scenario, actionTaken } = pendingAction;
    const responseTime = Date.now() - scenarioStartTime.current;
    const decisionTime = Date.now() - decisionStartTime.current;
    const responseId = `device-${scenario.id}-${Date.now()}`;
    
    // Build FTT context
    const fttContext = {
      // Do NOT feed ground-truth risk or stimulus complexity into the processing-mode
      // classifier — both are independent variables; letting them set the DV is circular.
      // (Matches the InformationSharingTask decontamination.) Mode stays exploratory;
      // confirmatory FTT uses the conjoint-recognition memory traces.
      isPhishing: false,
      scenarioComplexity: 0.5,
      priorResponses: Object.keys(responses).length,
      cognitiveLoad: calculateDeviceCognitiveLoad()
    };
    
    // Get FTT classification
    const fttClassification = classifyCurrentResponse(responseId, responseTime, fttContext);
    
    // Store in experiment context for cross-domain analysis
    updateFTTClassification('device', responseId, fttClassification);
    
    // Theoretical measures for cognitive depletion
    const cognitiveDepletion = {
      scenarioOrder: currentScenarioIndex + 1,
      cumulativeTaskTime: Date.now() - phaseStartTime.current,
      priorDeliberateDecisions: Object.values(responses).filter((r: any) => r.processingMode === 'deliberate').length,
      responseTimeAcceleration: currentScenarioIndex > 0 ? 
        responseTime - Object.values(responses)[currentScenarioIndex - 1]?.responseTime : 0
    };
    
    const response = {
      scenarioId: scenario.id,
      scenarioType: scenario.type,
      visualType: scenario.visualData?.type, // ADDED: Visual type for microsurvey
      domain: scenario.domain, // ADDED: Counterbalancing domain (DDom1-DDom8)
      isRisky: scenario.isRisky,
      congruence: scenario.congruence,
      gistCues: scenario.gistCues,
      verbatimCues: scenario.verbatimCues,
      conflictLevel: scenario.conflictLevel,
      // Canonical persistence keys.
      decision: actionTaken,
      correct: (scenario.isRisky && actionTaken === 'decline') || (!scenario.isRisky && actionTaken === 'proceed'),
      // Legacy aliases retained in the event payload while older stored data and
      // analysis code are migrated.
      actionTaken,
      presentationOrder: currentScenarioIndex + 1, // one-based order in which this scenario was shown
      taskHalf: currentScenarioIndex < 2 ? 'first' : 'second',
      correctAction: scenario.isRisky ? 'decline' : 'proceed',
      isCorrect: (scenario.isRisky && actionTaken === 'decline') || (!scenario.isRisky && actionTaken === 'proceed'),
      responseTime,
      decisionTime,
      processingMode: fttClassification.mode === 'gist' ? 'automatic' : 'deliberate',
      fttClassification,
      cognitiveDepletion,
      microSurveyResponse: surveyResponse || { reason: 'skipped' },
      timestamp: Date.now()
    };

    // Track the device action
    tracker.track('deviceAction', response);

    const nextResponses = { ...responses, [scenario.id]: response };
    const nextScenarios = scenarios.map(s =>
      s.id === scenario.id ? { ...s, decision: actionTaken } : s
    );

    // Store response and decision using the same snapshots that completion will save.
    setResponses(nextResponses);
    setScenarios(nextScenarios);

    // localStorage backup BEFORE navigation — last-resort copy (mirrors info-sharing).
    try {
      const pid = experimentData?.participant?.id || localStorage.getItem('participantId');
      if (pid) {
        localStorage.setItem(`deviceBackup_${pid}`, JSON.stringify({
          endTime: Date.now(),
          responses: Object.values(nextResponses)
        }));
      }
    } catch (e) { console.error('Could not write device backup:', e); }

    // Hide micro-survey and clear pending action
    setShowMicroSurvey(false);
    setPendingAction(null);

    // Show feedback based on condition
    console.log('[DeviceTask] Checking feedback condition:', experimentData?.participant?.conditions?.feedbackType);
    if (experimentData?.participant?.conditions?.feedbackType === 'immediate') {
      console.log('[DeviceTask] Showing immediate feedback');
      showImmediateFeedback(scenario, actionTaken, nextResponses, nextScenarios);
    } else {
      console.log('[DeviceTask] No immediate feedback, moving to next scenario');
      moveToNextScenario(nextResponses, nextScenarios);
    }
    console.log('[DeviceTask] processPendingAction completed');
  };

  const handleMicroSurveySubmit = (surveyResponse: DeviceMicroSurveyResponse) => {
    // Track microsurvey response separately for better data capture
    if (pendingAction) {
      tracker.track('microSurveyResponse', {
        taskType: 'device',
        deviceScenarioId: pendingAction.scenario.id,
        action: pendingAction.actionTaken,
        response: surveyResponse.reason,
        customReason: surveyResponse.customReason,
        reasonCategory: surveyResponse.reasonCategory,
        confidence: surveyResponse.confidence,
        metacognitiveCertainty: surveyResponse.metacognitiveCertainty,
        perceivedTimePressure: surveyResponse.perceivedTimePressure,
        rankedReasons: surveyResponse.rankedReasons,
        gistDominanceScore: surveyResponse.gistDominanceScore,
        presentedOrder: surveyResponse.presentedOrder,
        selectedIndices: surveyResponse.selectedIndices,
        primaryReasonKeySelected: surveyResponse.primaryReasonKeySelected,
        routeSelected: surveyResponse.routeSelected,
        gistOrder: surveyResponse.gistOrder,
        verbatimOrder: surveyResponse.verbatimOrder,
        selectedIndexWithinColumn: surveyResponse.selectedIndexWithinColumn,
        decisionLatencyMs: surveyResponse.decisionLatencyMs,
        totalMicroSurveyTimeMs: surveyResponse.totalMicroSurveyTimeMs,
        step1TimeMs: surveyResponse.step1TimeMs,
        step2TimeMs: surveyResponse.step2TimeMs,
        isRisky: pendingAction.scenario.isRisky,
        congruence: pendingAction.scenario.congruence,
        gistCues: pendingAction.scenario.gistCues,
        verbatimCues: pendingAction.scenario.verbatimCues,
        conflictLevel: pendingAction.scenario.conflictLevel,
        correctAction: pendingAction.scenario.isRisky ? 'decline' : 'proceed',
        phase: 'device-task'
      });
    }

    processPendingAction(surveyResponse);
  };

  const handleMicroSurveyClose = () => {
    // User cancelled - don't process the action
    setShowMicroSurvey(false);
    setPendingAction(null);
  };

  const showImmediateFeedback = (
    _scenario: DeviceScenario,
    _actionTaken: string,
    responseSnapshot: { [key: string]: any },
    scenarioSnapshot: DeviceScenario[]
  ) => {
    // Older participant assignments may still contain feedbackType=immediate.
    // Keep the transition working without revealing ground truth and teaching
    // participants how to answer later trials.
    setFeedbackMessage('Response recorded.');
    
    setShowFeedback(true);
    
    setTimeout(() => {
      setShowFeedback(false);
      moveToNextScenario(responseSnapshot, scenarioSnapshot);
    }, 800);
  };

  const moveToNextScenario = (
    responseSnapshot: { [key: string]: any } = responses,
    scenarioSnapshot: DeviceScenario[] = scenarios
  ) => {
    console.log('[DeviceTask] moveToNextScenario called');
    console.log('[DeviceTask] currentScenarioIndex:', currentScenarioIndex);
    console.log('[DeviceTask] scenarios.length:', scenarios.length);
    
    if (currentScenarioIndex < scenarios.length - 1) {
      console.log('[DeviceTask] Moving to next scenario');
      setCurrentScenarioIndex(prev => prev + 1);
      // Reset scenario start time for next scenario
      scenarioStartTime.current = Date.now();
    } else {
      console.log('[DeviceTask] All scenarios completed, completing phase');
      completePhase(responseSnapshot, scenarioSnapshot);
    }
  };

  const completePhase = async (
    responseSnapshot: { [key: string]: any } = responses,
    scenarioSnapshot: DeviceScenario[] = scenarios
  ) => {
    const responseList = Object.values(responseSnapshot);
    const summary = {
      totalScenarios: scenarioSnapshot.length,
      riskyScenarios: scenarioSnapshot.filter(s => s.isRisky).length,
      correctResponses: responseList.filter((r: any) => r.isCorrect).length,
      averageResponseTime: responseList.length > 0
        ? responseList.reduce((sum: number, r: any) => sum + r.responseTime, 0) / responseList.length
        : 0,
      congruenceCondition: 'balanced-2x2x2x2', // All participants receive balanced distribution
      scenarioDetails: scenarioSnapshot.map(s => ({
        id: s.id,
        type: s.type,
        isRisky: s.isRisky,
        congruence: s.congruence,
        conflictLevel: s.conflictLevel,
        decision: s.decision
      }))
    };

    // Track phase completion
    tracker.track('phaseComplete', {
      phase: 'device-task',
      duration: Date.now() - phaseStartTime.current,
      summary
    });

    // CRITICAL: Save device task data to database
    const participantId = experimentData?.participant?.id || localStorage.getItem('participantId');
    if (participantId) {
      // localStorage backup BEFORE navigation — last-resort copy until the save confirms.
      try {
        localStorage.setItem(`deviceBackup_${participantId}`, JSON.stringify({
          endTime: Date.now(), responses: responseList, summary
        }));
      } catch (e) { console.error('Could not write device backup:', e); }
      // Await so the save (or its localStorage retry-queue write) completes
      // before we navigate away and unmount.
      try {
        await savePhaseData(participantId, 'device-task', {
          ...summary,
          responses: responseList,
          completedAt: new Date().toISOString()
        });
        console.log('Device task data saved to database');
        // Confirmed persisted — safe to clear the backup.
        try { localStorage.removeItem(`deviceBackup_${participantId}`); } catch {}
      } catch (error) {
        console.error('Failed to save device task data (backup retained for recovery):', error);
      }
    }

    // Navigate based on two-cohort system
    const taskAssignment = experimentData?.participant?.taskAssignment;
    const cohort = taskAssignment?.cohort;
    const taskOrder = taskAssignment?.taskOrder;
    
    if (cohort === 'email-device') {
      if (taskOrder === 'secondary-first') {
        updateProgress(60); // 60% complete after first task
        navigate('/filler-task'); // Go to filler task before email task
      } else {
        updateProgress(70); // 70% complete after second task
        navigate('/post-experiment'); // Complete - go to post-experiment
      }
    } else {
      // This shouldn't happen for device task in email-coding cohort
      console.warn('DeviceTask completed but participant not in email-device cohort');
      navigate('/post-experiment');
    }
  };

  if (scenarios.length === 0) {
    return <div className="loading">Preparing device security scenarios...</div>;
  }

  const currentScenario = scenarios[currentScenarioIndex];
  const progress = ((currentScenarioIndex + 1) / scenarios.length) * 100;

  return (
    <div className="device-task" onMouseMove={trackMouseMovement}>
      <div className="scenario-counter">
        Scenario {currentScenarioIndex + 1} of {scenarios.length}
      </div>
      <div className="task-header">
        <h2>Device Security Assessment</h2>
      </div>

      <div className="scenario-container">
        <div className="scenario-content">
          <h3>{currentScenario.title}</h3>
          
          <div
            className="scenario-description"
            onMouseEnter={() => {
              tracker.track('contentEngagement', {
                element: 'scenario-description',
                scenarioId: currentScenario.id,
                startTime: Date.now()
              });
            }}
            onMouseLeave={() => {
              tracker.track('contentEngagement', {
                element: 'scenario-description',
                scenarioId: currentScenario.id,
                endTime: Date.now(),
                processingRoute: 'central' // They engaged with content
              });
            }}
          >
            <p><strong>Situation:</strong> {currentScenario.description}</p>
            {/* Context hidden - simulates real life where extra context isn't available */}
            {/* Context is still saved in data for analysis: {currentScenario.context} */}
          </div>

          {/* Rich visual component for modern threats */}
          {currentScenario.visualData && (
            <DeviceScenarioVisual
              type={currentScenario.visualData.type}
              data={currentScenario.visualData}
            />
          )}

          {/* Fallback icons for scenarios without visualData */}
          {!currentScenario.visualData && currentScenario.type === 'download' && (
            <div className="visual-element download-icon">
              <span role="img" aria-label="Download">⬇️</span>
            </div>
          )}

          {!currentScenario.visualData && currentScenario.type === 'permission' && (
            <div className="visual-element permission-icon">
              <span role="img" aria-label="Permission request">🔐</span>
            </div>
          )}

          {!currentScenario.visualData && currentScenario.type === 'network' && (
            <div className="visual-element network-icon">
              <span role="img" aria-label="Network">📶</span>
            </div>
          )}

          <div className="action-prompt">
            <p><strong>{currentScenario.decision ? 'You decided to:' : 'Would you:'}</strong></p>
            {currentScenario.decision && (
              <div className="decision-made">
                <span className={`decision-indicator ${currentScenario.decision}`}>
                  {currentScenario.decision === 'proceed' ? '✓ Proceeded' : '✗ Declined'}
                </span>
              </div>
            )}
            <div className="action-buttons">
              <button
                className={`btn btn-action proceed ${currentScenario.decision ? 'disabled' : ''}`}
                onClick={() => handleAction(currentScenario, 'proceed')}
                disabled={!!currentScenario.decision}
                onMouseEnter={() => {
                  decisionStartTime.current = Date.now();
                  trackHover('proceed-button', 'enter', {
                    scenarioId: currentScenario.id,
                    buttonType: 'proceed',
                    isRiskyScenario: currentScenario.isRisky
                  });
                }}
                onMouseLeave={() => {
                  trackHover('proceed-button', 'leave', {
                    dwellTime: Date.now() - decisionStartTime.current
                  });
                }}
              >
                {currentScenario.visualData?.buttons?.[0] || currentScenario.actionObj?.text || 'Proceed'}
              </button>
              <button
                className={`btn btn-action decline ${currentScenario.decision ? 'disabled' : ''}`}
                onClick={() => handleAction(currentScenario, 'decline')}
                disabled={!!currentScenario.decision}
                onMouseEnter={() => {
                  decisionStartTime.current = Date.now();
                  trackHover('decline-button', 'enter', {
                    scenarioId: currentScenario.id,
                    buttonType: 'decline',
                    isRiskyScenario: currentScenario.isRisky
                  });
                }}
                onMouseLeave={() => {
                  trackHover('decline-button', 'leave', {
                    dwellTime: Date.now() - decisionStartTime.current
                  });
                }}
              >
                {currentScenario.visualData?.buttons?.[1] || 'Decline'}
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Feedback modal */}
      {showFeedback && (
        <div className="feedback-overlay">
          <div className="feedback-modal">
            <p>{feedbackMessage}</p>
          </div>
        </div>
      )}

      {/* Skip button for testing */}
      {process.env.NODE_ENV === 'development' && (
        <button 
          className="btn btn-secondary skip-button"
          onClick={() => completePhase()}
        >
          Skip to Next Phase (Dev Only)
        </button>
      )}
      
      {/* Micro-survey for device decisions */}
      {showMicroSurvey && pendingAction && (
        <MicroSurvey
          taskType="device"
          scenarioType={pendingAction.scenario.visualData?.type}
          actionTaken={pendingAction.actionTaken}
          onSubmit={handleMicroSurveySubmit}
          onClose={handleMicroSurveyClose}
        />
      )}
    </div>
  );
};

export default DeviceTask;
